import React from "react";
import { reportClientError } from "../instrument";

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error | null;
  errorInfo?: React.ErrorInfo | null;
};

type ErrorBoundaryProps = React.PropsWithChildren<{
  /**
   * Rendered instead of the default diagnostic fallback. Use this on public
   * routes, where the error name, message and component stack must not be
   * shown to anonymous visitors.
   */
  fallback?: React.ReactNode;
}>;

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error("ErrorBoundary caught:", error, errorInfo);
    // Reuse the shared scrubber/field-limiter so payloads are normalized
    // identically to the global window error instrumentation.
    reportClientError({
      type: 'react-error-boundary',
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
    this.setState({ error, errorInfo });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="min-h-screen bg-white text-gray-900 p-6">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-xl font-semibold mb-2">Something went wrong.</h1>
            <p className="mb-4">An unexpected error occurred while rendering this page.</p>
            {this.state.error && (
              <div className="mb-4 p-3 rounded border bg-red-50 text-red-800">
                {this.state.error.name}: {this.state.error.message}
              </div>
            )}
            {this.state.errorInfo && (
              <pre className="whitespace-pre-wrap text-xs p-3 rounded border bg-gray-50 overflow-auto max-h-96">
                {this.state.errorInfo.componentStack}
              </pre>
            )}
            <button className="mt-4 px-3 py-2 border rounded" onClick={() => window.location.reload()}>Reload</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
